import React from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, IconButton, TextField } from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';

import ConfirmDialog from 'components/ConfirmDialog';
import SelectField from 'components/SelectField';

import { mapToSelectOptions } from 'utils/select';
import { getApiErrorMessage } from 'utils/errors';
import SaveError from 'components/common/SaveError';

import { StockMovementLossFormSchema } from './schemas/StockModuleForm';

import { getVenuesWithStockModuleActive } from 'services/venuesServices';
import { getCommercesByVenue } from 'services/commerceServices';
import {
  getVenueWarehouses,
  getWarehouseProducts,
  getCommerceProductsAvailable,
} from 'services/stockWarehouseServices';
import { storeStockLoss } from 'services/stockMovementsServices';

import Venue from 'types/models/Venue';
import {
  StockMovement,
  StockMovementReason,
  StockMovementSourceType,
  StockMovementItem,
} from 'types/models/StockMovement';
import { StockWarehouse, LocationProduct } from 'types/models/StockReceipt';
import Commerce from 'types/models/Commerce';
import AppContext from 'contexts/AppContext';
import useSetupInitialState from 'hooks/useSetupInitialState';
import UserRoleType from 'types/enums/UserRoleType';

interface Props {
  movement?: StockMovement | null;
  isReadOnly?: boolean;
}

const EMPTY_PRODUCT: StockMovementItem = {
  id: '',
  globalProductId: 0,
  quantity: 0,
  unitCost: 0,
  createdAt: new Date().toISOString().slice(0, 10),
  stockMovementId: '',
};

export default function StockLossForm({ movement, isReadOnly }: Props): JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();

  const { authData } = React.useContext(AppContext);
  const entity = useSetupInitialState();
  const isVenueEditor = authData?.user?.role === UserRoleType.VenueEditor;
  const [openDialog, setOpenDialog] = React.useState(false);

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [warehouses, setWarehouses] = React.useState<StockWarehouse[]>([]);
  const [commerces, setCommerces] = React.useState<Commerce[]>([]);

  const [locationProducts, setLocationProducts] = React.useState<LocationProduct[]>([]);
  const [products, setProducts] = React.useState<StockMovementItem[]>(
    movement?.items?.length ? movement.items : [{ ...EMPTY_PRODUCT }],
  );

  const [isLoading, setIsLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let mounted = true;

    async function loadInitialData() {
      if (isVenueEditor) {
        if (mounted) setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const [venuesData] = await Promise.all([getVenuesWithStockModuleActive()]);

        if (!mounted) return;

        setVenues(venuesData?.data ?? []);
      } catch (error) {
        console.error(error);

        if (!mounted) return;

        setLoadError(t('stock.receipts.error.no-necessary-data'));
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadInitialData();

    return () => {
      mounted = false;
    };
  }, [isVenueEditor]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      venueId: movement?.venueId ?? entity?.id ?? '',

      locationType: movement?.source?.type ?? StockMovementSourceType.WAREHOUSE,

      locationId: movement?.source?.id ?? '',

      reason: movement?.reason ?? StockMovementReason.DAMAGED,
    },
    validationSchema: () => StockMovementLossFormSchema(t),
    onSubmit: () => setOpenDialog(true),
  });

  React.useEffect(() => {
    if (!formik.values.venueId) {
      setWarehouses([]);
      setCommerces([]);
      return;
    }

    async function loadLocations() {
      try {
        const [warehousesData, commercesData] = await Promise.all([
          getVenueWarehouses(formik.values.venueId),
          getCommercesByVenue({ venueId: formik.values.venueId }),
        ]);

        setWarehouses(warehousesData);
        setCommerces(commercesData);
      } catch (error) {
        console.error(error);
      }
    }

    loadLocations();

    if (!movement) formik.setFieldValue('locationId', '');

    setLocationProducts([]);
    if (!movement) setProducts([{ ...EMPTY_PRODUCT }]);
  }, [formik.values.venueId]);

  React.useEffect(() => {
    if (!formik.values.locationId) {
      setLocationProducts([]);
      if (!movement) setProducts([{ ...EMPTY_PRODUCT }]);
      return;
    }

    async function loadProducts() {
      try {
        if (formik.values.locationType === StockMovementSourceType.WAREHOUSE) {
          const data = await getWarehouseProducts(formik.values.locationId);
          setLocationProducts(data);
        } else {
          const data = await getCommerceProductsAvailable(formik.values.locationId);
          setLocationProducts(data);
        }

        if (!movement) setProducts([{ ...EMPTY_PRODUCT }]);
      } catch (error) {
        console.error(error);
        setLocationProducts([]);
      }
    }

    loadProducts();
  }, [formik.values.locationType, formik.values.locationId]);

  function updateProduct(index: number, key: keyof StockMovementItem, value: any) {
    const copy = [...products];

    copy[index] = {
      ...copy[index],
      [key]: value,
    };

    setProducts(copy);
  }

  function selectProduct(index: number, globalProductId: number) {
    const selected = locationProducts.find((p) => p.globalProductId === globalProductId);

    if (!selected) return;

    const copy = [...products];

    copy[index] = {
      ...copy[index],
      id: selected.commerceProductId ?? '',
      globalProductId: selected.globalProductId,
      quantity: 1,
      unitCost: 0,
    };

    setProducts(copy);
  }

  function addProduct() {
    setProducts((prev) => [...prev, { ...EMPTY_PRODUCT }]);
  }

  function removeProduct(index: number) {
    if (products.length === 1) return;

    setProducts(products.filter((_, i) => i !== index));
  }

  function clearProduct(index: number) {
    const copy = [...products];
    copy[index] = { ...EMPTY_PRODUCT };
    setProducts(copy);
  }

  async function onConfirm() {
    setSaveError(null);

    try {
      await storeStockLoss({
        venueId: formik.values.venueId,
        locationType: formik.values.locationType,
        locationId: formik.values.locationId,
        reason: formik.values.reason,
        products,
        createdByUserId: authData?.user.id,
      });

      history.go(-1);
    } catch (error) {
      console.error(error);
      setSaveError(getApiErrorMessage(error) ?? t('errors.generic'));
    } finally {
      formik.setSubmitting(false);
    }
  }

  if (isLoading) return <div>{t('loading')}</div>;

  if (loadError) return <div>{loadError}</div>;

  return (
    <>
      <ConfirmDialog
        open={openDialog}
        setOpen={setOpenDialog}
        onConfirm={onConfirm}
        onCancel={() => formik.setSubmitting(false)}
      />

      <form onSubmit={formik.handleSubmit}>
        {!isVenueEditor && (
          <SelectField
            name="venueId"
            label={t('stock.forms.venue')}
            placeholder={t('stock.forms.venue-placeholder')}
            value={formik.values.venueId}
            options={mapToSelectOptions(venues, { label: 'name' })}
            isMulti={false}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.venueId}
            error={formik.errors.venueId}
            isClearable={false}
            backspaceRemovesValue={false}
            isDisabled={formik.isSubmitting}
            isSearchable
          />
        )}

        <SelectField
          name="locationType"
          label={t('stock.forms.location')}
          placeholder={t('stock.forms.select')}
          value={formik.values.locationType}
          options={[
            {
              label: t('stock.forms.warehouse'),
              value: StockMovementSourceType.WAREHOUSE,
            },
            {
              label: t('stock.forms.commerce'),
              value: StockMovementSourceType.COMMERCE,
            },
          ]}
          isMulti={false}
          setFieldValue={(name, value) => {
            formik.setFieldValue(name, value);
            if (!movement) formik.setFieldValue('locationId', '');
          }}
          onBlur={formik.handleBlur}
          touched={formik.touched.locationType}
          error={formik.errors.locationType}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={false}
        />

        <SelectField
          name="locationId"
          label={
            formik.values.locationType === StockMovementSourceType.WAREHOUSE
              ? t('stock.forms.warehouse')
              : t('stock.forms.commerce')
          }
          placeholder={t('stock.forms.select')}
          value={formik.values.locationId}
          options={mapToSelectOptions(
            formik.values.locationType === StockMovementSourceType.WAREHOUSE ? warehouses : (commerces as any),
            { label: 'name' },
          )}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.locationId}
          error={formik.errors.locationId}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable
        />

        <SelectField
          name="reason"
          placeholder={t('stock.forms.reason-placeholder')}
          label={t('stock.forms.reason')}
          value={formik.values.reason}
          options={[
            {
              label: t('stock.loss-reasons.DAMAGED'),
              value: StockMovementReason.DAMAGED,
            },
            {
              label: t('stock.loss-reasons.EXPIRED'),
              value: StockMovementReason.EXPIRED,
            },
            {
              label: t('stock.loss-reasons.INVENTORY_COUNT'),
              value: StockMovementReason.INVENTORY_COUNT,
            },
            {
              label: t('stock.loss-reasons.MANUAL_ADJUSTMENT'),
              value: StockMovementReason.MANUAL_ADJUSTMENT,
            },
          ]}
          isMulti={false}
          setFieldValue={formik.setFieldValue}
          onBlur={formik.handleBlur}
          touched={formik.touched.reason}
          error={formik.errors.reason}
          isClearable={false}
          backspaceRemovesValue={false}
          isDisabled={formik.isSubmitting}
          isSearchable={false}
        />

        <h3>{t('stock.forms.products')}</h3>

        {products.map((product, index) => {
          const selected = locationProducts.find((p) => p.globalProductId === product.globalProductId);

          return (
            <div
              key={index}
              style={{
                border: '1px solid #DDD',
                padding: 15,
                marginBottom: 15,
              }}
            >
              <SelectField
                name={`product-${index}`}
                label={t('stock.forms.product')}
                placeholder={t('stock.forms.select')}
                value={product.globalProductId}
                options={locationProducts.map((p) => ({
                  value: p.globalProductId,
                  label: `${p.name} (${p.sku ?? ''})`,
                }))}
                isMulti={false}
                setFieldValue={(_, value) => selectProduct(index, value)}
                touched={false}
                error={false}
                isDisabled={formik.isSubmitting}
                isClearable
                isSearchable
                backspaceRemovesValue
                onBlur={() => undefined}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.sku')}
                value={selected?.sku ?? ''}
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.name')}
                value={selected?.name ?? ''}
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                label={t('stock.forms.available-stock')}
                value={selected?.stock ?? 0}
                InputProps={{
                  readOnly: true,
                }}
              />

              <TextField
                margin="dense"
                fullWidth
                type="number"
                label={t('stock.forms.quantity')}
                value={product.quantity}
                onChange={(e) => updateProduct(index, 'quantity', Number(e.target.value))}
                InputProps={{
                  readOnly: !!movement,
                }}
                inputProps={{
                  max: selected?.stock,
                }}
              />
              <div
                style={{
                  marginTop: 10,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div />

                {movement ? null : (
                  <div>
                    <IconButton color="primary" onClick={() => clearProduct(index)}>
                      <Delete />
                    </IconButton>

                    <IconButton color="primary" onClick={addProduct}>
                      <Add />
                    </IconButton>

                    {products.length > 1 && (
                      <IconButton color="secondary" onClick={() => removeProduct(index)}>
                        <Delete />
                      </IconButton>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        <SaveError error={saveError} />

        {!isReadOnly && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 25,
            }}
          >
            <Button variant="contained" color="primary" type="submit" disabled={formik.isSubmitting}>
              {t('stock.forms.create-loss')}
            </Button>
          </div>
        )}
      </form>
    </>
  );
}
